import styled from "@emotion/styled";
import classNames from "classnames";
import { get } from "es-toolkit/compat";
import { useMemo, type CSSProperties } from "react";

import type { UseDroppableInput } from "@skilitics/drag-drop";
import { useDragOperation, useDroppable } from "@skilitics/drag-drop";

import { showDndDebugInfo } from "./OrderableItem";

type DropzoneColor = "primary" | "secondary" | "warning";

export const DroppableContainer = styled.div<{ $hideBorder: boolean; $color: DropzoneColor }>`
  width: 100%;
  height: 100%;
  ${({ $hideBorder }) => (!$hideBorder ? `outline: solid 2px transparent;` : "")}
  position: relative;
  transition: all 0.3s ease-in;
  border-radius: 8px;
  &.over-allowed:before {
    opacity: 0.2;
  }
  &.not-over-allowed:before {
    opacity: 0.05;
  }
  &:before {
    border-radius: 8px;
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    pointer-events: none;
    z-index: 1;
    transition: opacity 0.2s ease-in-out;
    opacity: 0;
    background: ${({ theme, $color }) => theme[$color]};
  }
`;
interface DroppableProps extends UseDroppableInput {
  style?: CSSProperties;
  children?: React.ReactNode;
  hideBorder?: boolean;
  accepts?: Record<string, string[]>;
  color?: "primary" | "secondary" | "warning";
}
export const Droppable = ({
  style,
  children,
  hideBorder = false,
  accepts,
  color,
  ...droppableProps
}: DroppableProps) => {
  const { source: active } = useDragOperation();

  const { ref, isDropTarget } = useDroppable({
    ...droppableProps,
  });

  const allowedOver = useMemo(() => {
    if (!droppableProps.disabled && accepts) {
      for (const key of Object.keys(accepts)) {
        const activeData = get(active?.data, key);
        if (activeData && (get(accepts, key) ?? []).includes(activeData)) {
          return true;
        }
      }
    }
    return false;
  }, [droppableProps.disabled, accepts, active?.data]);

  return (
    <DroppableContainer
      id={droppableProps.id?.toString()}
      ref={ref}
      style={style}
      className={classNames({
        "over-allowed": allowedOver && isDropTarget,
        "not-over-allowed": allowedOver && !isDropTarget,
        "not-allowed": active && !allowedOver,
        [color ?? "primary"]: true,
      })}
      $hideBorder={hideBorder}
      $color={color ?? "primary"}
      data-test="droppable-container"
    >
      {showDndDebugInfo && `Droppable: ${droppableProps.id}`}
      {children}
    </DroppableContainer>
  );
};
